import { apiClient } from './api-client';

export async function registerPushNotifications(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

  if (Notification.permission === 'denied') return;

  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;
  }

  try {
    navigator.serviceWorker.register('/sw.js').catch(err => {
      console.error('[push] SW register error:', err);
    });

    // Wait for the SW to be fully active before subscribing
    const registration = await navigator.serviceWorker.ready;

    const existingSub = await registration.pushManager.getSubscription();
    if (existingSub) {
      // Already subscribed — ensure the backend has it
      const json = existingSub.toJSON() as {
        endpoint: string;
        keys: { p256dh: string; auth: string };
      };
      await apiClient.post('/push-subscriptions', { endpoint: json.endpoint, keys: json.keys });
      return;
    }

    const { publicKey } = await apiClient.get<{ publicKey: string }>(
      '/push-subscriptions/vapid-public-key'
    );
    const applicationServerKey = urlBase64ToUint8Array(publicKey);

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    const json = subscription.toJSON() as {
      endpoint: string;
      keys: { p256dh: string; auth: string };
    };

    await apiClient.post('/push-subscriptions', {
      endpoint: json.endpoint,
      keys: json.keys,
    });
  } catch (err) {
    console.error('[push] registration failed:', err);
  }
}

export async function unregisterPushNotifications(): Promise<void> {
  try {
    await apiClient.del('/push-subscriptions');
  } catch {
    // best effort — server-side removal may fail if already gone
  }

  if (!('serviceWorker' in navigator)) return;

  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!registration) return;

  const subscription = await registration.pushManager.getSubscription();
  if (subscription) await subscription.unsubscribe();
}

// El tipo de retorno fija el buffer a `ArrayBuffer`: `applicationServerKey` pide
// un `BufferSource`, que no acepta un `Uint8Array` respaldado por
// `SharedArrayBuffer`, y `Uint8Array.from` devuelve el genérico `ArrayBufferLike`.
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; i += 1) {
    bytes[i] = rawData.charCodeAt(i);
  }
  return bytes;
}
